import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const NAMESPACES = [
  'common',
  'topHeader',
  'transport',
  'viewer',
  'timeline',
  'comments',
  'script',
  'aiWorkspace',
  'aiTts',
  'audioWorkspace',
  'account',
  'sharing',
  'fileLoader',
  'help',
  'export',
  'screenplayManager',
  'screenplayImport',
  'generationHistory',
  'errors',
  'landing',
  'onboarding',
  'previsAssist',
  'avatar',
  'projects',
  'tutorial',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

const SUPPORTED_LOCALES = [
  'en',
  'sv',
  'it',
  'es',
  'fr',
  'de',
  'pt-BR',
  'ja',
  'zh-Hans',
  'zh-Hant',
  'ko',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// English always bundled (fallback language, ~85KB)
const enModules = import.meta.glob<Record<string, string>>(
  '../localization/locales/.compiled/en/*.json',
  { eager: true }
);

// Other languages loaded on demand — Vite creates separate chunks
const lazyModules = import.meta.glob<Record<string, string>>([
  '../localization/locales/.compiled/**/*.json',
  '!../localization/locales/.compiled/en/**',
]);

// Build resources from eagerly-loaded English files only
function buildEnglishResources(): Record<string, Record<string, Record<string, string>>> {
  const resources: Record<string, Record<string, Record<string, string>>> = {};

  for (const [filePath, mod] of Object.entries(enModules)) {
    const ns = filePath.match(/([^/]+)\.json$/)?.[1];
    if (!ns) continue;
    if (!resources.en) resources.en = {};
    resources.en[ns] = (mod as { default?: Record<string, string> }).default ?? (mod as Record<string, string>);
  }

  return resources;
}

// Lazy-load a non-English language and register its bundles with i18next
const loadedLanguages = new Set<string>(['en']);

async function loadLanguage(lang: string): Promise<void> {
  if (loadedLanguages.has(lang)) return;
  const imports = Object.entries(lazyModules)
    .filter(([p]) => p.includes(`/.compiled/${lang}/`));
  if (imports.length === 0) return;
  const results = await Promise.all(
    imports.map(async ([filePath, load]) => {
      const mod = await load();
      const ns = filePath.match(/([^/]+)\.json$/)?.[1];
      return [ns, mod.default ?? mod] as const;
    })
  );
  for (const [ns, data] of results) {
    if (ns) i18n.addResourceBundle(lang, ns, data, true, true);
  }
  loadedLanguages.add(lang);
}

// Embedded mode: Unity controls the locale via setLocale bridge message.
// Non-embedded: detect from URL ?lng= → localStorage → navigator.language → fallback en.
const isEmbedded =
  typeof window !== 'undefined' &&
  (window.location.protocol === 'file:' || '__previsRecv' in window);

// Suppress i18next's built-in Locize sponsorship console message during init
const _log = console.log;
console.log = (...args: unknown[]) => {
  if (typeof args[0] === 'string' && args[0].includes('locize')) return;
  _log.apply(console, args);
};

const instance = i18n.use(initReactI18next);

// Only use browser language detector in non-embedded mode.
// Embedded mode starts in 'en' and waits for Unity's setLocale message.
if (!isEmbedded) {
  instance.use(LanguageDetector);
}

instance.init({
  resources: buildEnglishResources(),
  supportedLngs: [...SUPPORTED_LOCALES],
  ...(isEmbedded ? { lng: 'en' } : {}), // Let detector pick language in non-embedded mode
  fallbackLng: 'en',
  ns: [...NAMESPACES],
  defaultNS: 'common',
  detection: {
    order: ['querystring', 'localStorage', 'navigator'],
    lookupQuerystring: 'lng',
    caches: ['localStorage'],
  },
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false,
  },
});

console.log = _log;

// Non-embedded: if detector picked a non-English language, load it immediately
if (!isEmbedded) {
  const detected = i18n.language;
  if (detected && detected !== 'en' && (SUPPORTED_LOCALES as readonly string[]).includes(detected)) {
    loadLanguage(detected).then(() => i18n.changeLanguage(detected));
  }
}

// Expose for console testing: window.__i18n.changeLanguage('ja')
// Wrap changeLanguage to lazy-load the target language first
const _origChangeLanguage = i18n.changeLanguage.bind(i18n);
(window as unknown as Record<string, unknown>).__i18n = Object.assign(
  Object.create(i18n),
  {
    changeLanguage: async (lng: string) => {
      await loadLanguage(lng);
      return _origChangeLanguage(lng);
    },
  }
);

// Allow the localization editor iframe host to control the app's language
window.addEventListener('message', async (event: MessageEvent) => {
  if (event.data?.type === 'i18n:setLocale' && typeof event.data.locale === 'string') {
    await loadLanguage(event.data.locale);
    i18n.changeLanguage(event.data.locale);
  }
  // Live preview: update a single translation string without reload
  if (event.data?.type === 'i18n:updateString') {
    const { lang, namespace, key, value } = event.data;
    i18n.addResourceBundle(lang, namespace, { [key]: value }, true, true);
    i18n.changeLanguage(i18n.language);
  }
});

export default i18n;
export { loadLanguage, NAMESPACES, SUPPORTED_LOCALES };
